import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Segmented,
  Select,
  Switch,
  Typography,
} from "antd";
import type { FormInstance } from "antd";
import dayjs from "dayjs";
import apiClient from "../../api/apiClient";
import type usePaymentFormChoices from "../../gestion-payments/usePaymentFormChoices";
import { useCommercialTreasuryAccountSuggestion } from "../../hooks/useCommercialTreasurySuggestion";

const { Text } = Typography;

export type OrderPaymentType = "in" | "out";

interface PaymentOption {
  id: number;
  name: string;
  company_id?: number | null;
  is_default?: boolean | number;
}

interface OrderInitialPaymentFieldsProps {
  choiceResource?: ReturnType<typeof usePaymentFormChoices>;
  form: FormInstance;
  total: number;
  companyId?: number | string | null;
  warehouseId?: number | string | null;
  canCreatePayment: boolean;
  disabled?: boolean;
  description: string;
  currencyLabel?: string;
  presentation?: "card" | "summary";
  inactiveTitle?: string;
  sectionTitle?: string;
  inactiveOptionLabel?: string;
  activeOptionLabel?: string;
}

interface BuildInitialPaymentPayloadOptions {
  paymentType: OrderPaymentType;
  defaultNotes: string;
}

const normalizePositiveId = (value: unknown): number | null => {
  const source =
    value && typeof value === "object" && "id" in value
      ? (value as { id?: unknown }).id
      : value;
  const parsed = Number(source);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const extractPaymentModes = (payload: any): PaymentOption[] => {
  const values = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.paymentModes)
      ? payload.paymentModes
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

  return values
    .filter((mode: any) => mode && Number(mode.id) > 0 && mode.active !== false)
    .map((mode: any) => ({
      id: Number(mode.id),
      name: String(mode.name || "Mode de paiement"),
      company_id: mode.company_id ?? null,
    }))
    .sort((left: PaymentOption, right: PaymentOption) => {
      if (left.company_id && !right.company_id) return -1;
      if (!left.company_id && right.company_id) return 1;
      return left.name.localeCompare(right.name, "fr");
    });
};

const extractTreasuryAccounts = (payload: any): PaymentOption[] => {
  const values = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return values
    .filter((account: any) => account && Number(account.id) > 0)
    .map((account: any) => ({
      id: Number(account.id),
      name: String(account.name || "Compte de trésorerie"),
      is_default: account.is_default,
    }));
};

export const buildInitialPaymentPayload = (
  values: any,
  { paymentType, defaultNotes }: BuildInitialPaymentPayloadOptions,
) => {
  if (!values?.add_initial_payment) return undefined;

  const payment = values.initial_payment || {};
  return {
    enabled: true,
    payment_type: paymentType,
    date: dayjs(payment.date).format("YYYY-MM-DD"),
    amount: Number(payment.amount),
    payment_mode_id: Number(payment.payment_mode_id),
    treasury_account_id: Number(payment.treasury_account_id),
    notes: String(payment.notes || defaultNotes).trim(),
  };
};

const OrderInitialPaymentFields: React.FC<OrderInitialPaymentFieldsProps> = ({
  form,
  total,
  companyId,
  warehouseId,
  canCreatePayment,
  disabled = false,
  description,
  currencyLabel = "CFA",
  presentation = "card",
  inactiveTitle = "Document sans paiement",
  sectionTitle = "Paiement",
  inactiveOptionLabel = "Sans paiement",
  activeOptionLabel = "Ajouter un paiement",
  choiceResource,
}) => {
  const enabled = Boolean(Form.useWatch("add_initial_payment", form));
  const watchedAmount = Number(
    Form.useWatch(["initial_payment", "amount"], form),
  );
  const [legacyModes, setPaymentModes] = useState<PaymentOption[]>([]);
  const [legacyAccounts, setTreasuryAccounts] = useState<PaymentOption[]>([]);
  const usesSharedChoices = Boolean(choiceResource);
  const sharedModes = choiceResource?.paymentModes;
  const sharedAccounts = choiceResource?.treasuryAccounts;
  const paymentModes = useMemo(() => usesSharedChoices ? extractPaymentModes(sharedModes) : legacyModes,
    [usesSharedChoices, sharedModes, legacyModes]);
  const treasuryAccounts = useMemo(() => usesSharedChoices ? extractTreasuryAccounts(sharedAccounts) : legacyAccounts,
    [usesSharedChoices, sharedAccounts, legacyAccounts]);
  const { onModeChange: suggestTreasuryAccount } =
    useCommercialTreasuryAccountSuggestion(form, {
      warehouseId,
      enabled: enabled && canCreatePayment && !disabled,
      modeField: ["initial_payment", "payment_mode_id"],
      accountField: ["initial_payment", "treasury_account_id"],
      accounts: treasuryAccounts,
    });
  const [legacyLoading, setLoadingOptions] = useState(false);
  const [legacyError, setLoadError] = useState<string | null>(null);
  const loadingOptions = choiceResource?.loading ?? legacyLoading;
  const loadError = choiceResource?.error ? "Impossible d’actualiser les choix du paiement. La saisie est conservée." : legacyError;
  const [reloadToken, setReloadToken] = useState(0);
  const automaticAmountRef = useRef<number | null>(null);

  const normalizedCompanyId = useMemo(
    () => normalizePositiveId(companyId),
    [companyId],
  );
  const normalizedWarehouseId = useMemo(
    () => normalizePositiveId(warehouseId),
    [warehouseId],
  );
  const normalizedTotal = Math.max(0, Number(total) || 0);
  const remainingBalance = Math.max(
    0,
    normalizedTotal - (Number.isFinite(watchedAmount) ? watchedAmount : 0),
  );
  const configurationReady = Boolean(
    normalizedCompanyId && normalizedWarehouseId,
  );

  useEffect(() => {
    if (usesSharedChoices || !enabled || !canCreatePayment || disabled || !configurationReady) {
      return;
    }

    let cancelled = false;
    setLoadingOptions(true);
    setLoadError(null);

    Promise.all([
      apiClient.get("/api/payment-modes", {
        params: { company_id: normalizedCompanyId, limit: 200 },
      }),
      apiClient.get("/api/treasury/accounts", {
        params: {
          company_id: normalizedCompanyId,
          warehouse_id: normalizedWarehouseId,
          include_inactive: false,
          module_scope: "commercial",
        },
      }),
    ])
      .then(([modeResponse, accountResponse]) => {
        if (cancelled) return;
        const modes = extractPaymentModes(modeResponse.data);
        const accounts = extractTreasuryAccounts(accountResponse.data);
        setPaymentModes(modes);
        setTreasuryAccounts(accounts);

      })
      .catch((error: any) => {
        if (cancelled) return;
        console.error(
          "[OrderInitialPaymentFields] Chargement des options impossible:",
          error,
        );
        setPaymentModes([]);
        setTreasuryAccounts([]);
        setLoadError(
          "Impossible de charger les modes de paiement ou les comptes de trésorerie.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    canCreatePayment,
    configurationReady,
    disabled,
    enabled,
    form,
    normalizedCompanyId,
    normalizedWarehouseId,
    reloadToken,
    usesSharedChoices,
  ]);

  useEffect(() => {
    if (!enabled || normalizedTotal <= 0) return;

    const currentAmount = Number(
      form.getFieldValue(["initial_payment", "amount"]),
    );
    if (
      !Number.isFinite(currentAmount) ||
      currentAmount <= 0 ||
      (!usesSharedChoices && currentAmount > normalizedTotal) ||
      (currentAmount === automaticAmountRef.current && currentAmount !== normalizedTotal)
    ) {
      const currentPayment = form.getFieldValue("initial_payment") || {};
      form.setFieldsValue({
        initial_payment: { ...currentPayment, amount: normalizedTotal },
      });
      automaticAmountRef.current = normalizedTotal;
    }
  }, [enabled, form, normalizedTotal, usesSharedChoices]);

  const handleToggle = (checked: boolean) => {
    if (!checked) {
      automaticAmountRef.current = null;
      return;
    }

    const currentPayment = form.getFieldValue("initial_payment") || {};
    const currentAmount = Number(currentPayment.amount);
    const amount =
      Number.isFinite(currentAmount) &&
      currentAmount > 0 &&
      currentAmount <= normalizedTotal
        ? currentAmount
        : normalizedTotal;
    automaticAmountRef.current = amount;
    form.setFieldsValue({
      initial_payment: {
        ...currentPayment,
        amount,
        date: currentPayment.date || dayjs(),
      },
    });
  };

  const paymentFields = (
    <>
      {!configurationReady ? (
        <Text type="secondary">
          Sélectionnez d’abord l’entreprise et l'établissement.
        </Text>
      ) : enabled ? (
        <>
          {loadError && (
            <Alert
              type="error"
              showIcon
              message={loadError}
              action={
                <Button size="small" onClick={() => {
                  if (choiceResource) void choiceResource.refresh().catch(() => {});
                  else setReloadToken((value) => value + 1);
                }}>
                  Réessayer
                </Button>
              }
              style={{ marginBottom: 12 }}
            />
          )}
          {!loadingOptions &&
            !loadError &&
            (paymentModes.length === 0 || treasuryAccounts.length === 0) && (
              <Alert
                type="warning"
                showIcon
                message="Configuration de paiement incomplète"
                description="Désactivez le paiement pour enregistrer uniquement le document, ou configurez un mode et un compte de trésorerie pour cet établissement."
                style={{ marginBottom: 12 }}
              />
            )}
          <Row gutter={[12, 4]} className="order-initial-payment__fields">
            <Col
              xs={24}
              sm={12}
              md={6}
              className="order-initial-payment__field-col order-initial-payment__field-col--date"
            >
              <Form.Item
                name={["initial_payment", "date"]}
                label="Date du paiement"
                rules={[
                  { required: true, message: "Sélectionnez la date du paiement" },
                ]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={12}
              md={6}
              className="order-initial-payment__field-col order-initial-payment__field-col--amount"
            >
              <Form.Item
                name={["initial_payment", "amount"]}
                label="Montant"
                rules={[
                  { required: true, message: "Saisissez le montant du paiement" },
                  {
                    validator: (_, value) => {
                      const amount = Number(value);
                      if (!Number.isFinite(amount) || amount <= 0) {
                        return Promise.reject(
                          new Error("Le montant doit être supérieur à zéro"),
                        );
                      }
                      if (amount > normalizedTotal) {
                        return Promise.reject(
                          new Error("Le montant dépasse le total du document"),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  min={0.01}
                  max={normalizedTotal}
                  precision={2}
                  addonAfter={currencyLabel}
                  style={{ width: "100%" }}
                  formatter={(value) =>
                    value === undefined || value === null
                      ? ""
                      : String(value).replace(/\B(?=(\d{3})+(?!\d))/g, " ")
                  }
                  parser={(value) =>
                    value ? Number(value.replace(/\s/g, "").replace(",", ".")) : 0
                  }
                  onChange={() => {
                    automaticAmountRef.current = null;
                  }}
                />
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={12}
              md={6}
              className="order-initial-payment__field-col order-initial-payment__field-col--mode"
            >
              <Form.Item
                name={["initial_payment", "payment_mode_id"]}
                label="Mode de paiement"
                rules={[
                  { required: true, message: "Sélectionnez un mode de paiement" },
                ]}
              >
                <Select
                  onChange={suggestTreasuryAccount}
                  showSearch
                  optionFilterProp="label"
                  loading={loadingOptions}
                  disabled={paymentModes.length === 0}
                  placeholder="Choisir un mode"
                  options={paymentModes.map((mode) => ({
                    value: mode.id,
                    label: `${mode.name}${mode.company_id ? "" : " · Général"}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col
              xs={24}
              sm={12}
              md={6}
              className="order-initial-payment__field-col order-initial-payment__field-col--account"
            >
              <Form.Item
                name={["initial_payment", "treasury_account_id"]}
                label="Compte de trésorerie"
                rules={[
                  {
                    required: true,
                    message: "Sélectionnez un compte de trésorerie",
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={loadingOptions}
                  disabled={treasuryAccounts.length === 0}
                  placeholder="Choisir un compte"
                  options={treasuryAccounts.map((account) => ({
                    value: account.id,
                    label: account.name,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name={["initial_payment", "notes"]}
                label="Note du paiement (facultative)"
                style={{ marginBottom: 0 }}
              >
                <Input.TextArea rows={2} maxLength={10000} />
              </Form.Item>
            </Col>
          </Row>
          <div
            className="order-initial-payment__balance"
            aria-live="polite"
          >
            <div>
              <span>Total du document</span>
              <strong>
                {normalizedTotal.toLocaleString("fr-FR")} {currencyLabel}
              </strong>
            </div>
            <div
              className={
                remainingBalance <= 0
                  ? "order-initial-payment__balance-row--settled"
                  : undefined
              }
            >
              <span>Solde restant</span>
              <strong>
                {remainingBalance.toLocaleString("fr-FR")} {currencyLabel}
              </strong>
            </div>
          </div>
        </>
      ) : null}
    </>
  );

  const hasPayableTotal = normalizedTotal > 0;
  const inactiveMessage = hasPayableTotal
    ? inactiveTitle
    : "Aucun montant à encaisser";
  const unpaidDescription = hasPayableTotal
    ? `Le solde de ${normalizedTotal.toLocaleString("fr-FR")} ${currencyLabel} restera dû.`
    : "Ajoutez au moins un article pour activer l'encaissement.";

  if (!canCreatePayment || disabled) {
    if (presentation !== "summary") return null;

    return (
      <section className="order-initial-payment order-initial-payment--summary">
        <div className="order-initial-payment__heading">
          <strong>{sectionTitle}</strong>
        </div>
        <Alert
          type="warning"
          showIcon
          message={inactiveMessage}
          description={unpaidDescription}
        />
      </section>
    );
  }

  if (presentation === "summary") {
    return (
      <section className="order-initial-payment order-initial-payment--summary">
        <div className="order-initial-payment__heading">
          <strong>{sectionTitle}</strong>
          <Text type="secondary">Facultatif</Text>
        </div>
        <Form.Item
          name="add_initial_payment"
          valuePropName="checked"
          initialValue={false}
          hidden
        >
          <Switch />
        </Form.Item>
        <div className="order-initial-payment__choice-wrapper">
          <Segmented
            className="order-initial-payment__choice"
            aria-label={`Choix : ${sectionTitle}`}
            block
            value={enabled ? "with-payment" : "without-payment"}
            options={[
              { label: inactiveOptionLabel, value: "without-payment" },
              {
                label: activeOptionLabel,
                value: "with-payment",
                disabled: !configurationReady || normalizedTotal <= 0,
              },
            ]}
            onChange={(value) => {
              const checked = value === "with-payment";
              form.setFieldValue("add_initial_payment", checked);
              handleToggle(checked);
            }}
          />
        </div>
        {!enabled ? (
          <Alert
            className="order-initial-payment__inactive"
            type="warning"
            showIcon
            message={inactiveMessage}
            description={unpaidDescription}
          />
        ) : (
          <div className="order-initial-payment__details">
            <Text type="secondary" className="order-initial-payment__description">
              {description}
            </Text>
            {paymentFields}
          </div>
        )}
      </section>
    );
  }

  return (
    <Card
      size="small"
      style={{ marginTop: 12, borderColor: enabled ? "#91caff" : undefined }}
      bodyStyle={{ padding: enabled ? "12px" : "8px 12px" }}
      title={
        <div>
          <div style={{ fontWeight: 600 }}>{activeOptionLabel}</div>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {description}
          </Text>
        </div>
      }
      extra={
        <Form.Item
          name="add_initial_payment"
          valuePropName="checked"
          initialValue={false}
          style={{ margin: 0 }}
        >
          <Switch
            aria-label={activeOptionLabel}
            disabled={!configurationReady || normalizedTotal <= 0}
            onChange={handleToggle}
          />
        </Form.Item>
      }
    >
      {paymentFields}
    </Card>
  );
};

export default OrderInitialPaymentFields;
